/** CORE checks read only the module's raw state (used by isShipPowered on
 *  the cockpit). SHIP checks may read ship.powered, so run after it settles. */
import type { ModuleDef, Ship, ShipModule, World } from "./types";

interface CheckCtx {
  readonly world: World;
  readonly ship: Ship;
  readonly m: ShipModule;
  readonly def: ModuleDef;
}

type DisableCheck = (ctx: CheckCtx) => string | null;

const CORE_CHECKS: readonly DisableCheck[] = [
  ({ m }) => (m.hp <= 0 ? "destroyed" : null),
  ({ m }) => (m.userOff === true ? "off" : null),
  ({ m, def }) => (def.stats.crew > 0 && m.manned === false ? "no crew" : null),
  ({ m, def }) => (def.category === "reactor" && m.fuelStarved === true ? "no fuel" : null),
  ({ m, world }) =>
    world.time < (m.rebootAt ?? 0)
      ? `rebooting ${(m.rebootAt! - world.time).toFixed(1)}s`
      : null,
  ({ m }) => (m.awaitingPower === true ? "no power" : null),
];

const SHIP_CHECKS: readonly DisableCheck[] = [
  ({ ship, def }) =>
    def.stats.requiresCockpit && !ship.powered ? "cockpit down" : null,
];

const ALL_CHECKS: readonly DisableCheck[] = [...CORE_CHECKS, ...SHIP_CHECKS];

function ctxOf(world: World, ship: Ship, m: ShipModule): CheckCtx {
  return { world, ship, m, def: m.def };
}

function passesAll(checks: readonly DisableCheck[], ctx: CheckCtx): boolean {
  for (let i = 0; i < checks.length; i++) if (checks[i](ctx) !== null) return false;
  return true;
}

export function isModuleDisabled(world: World, ship: Ship, m: ShipModule): boolean {
  const ctx = ctxOf(world, ship, m);
  for (let i = 0; i < ALL_CHECKS.length; i++) {
    if (ALL_CHECKS[i](ctx) !== null) return true;
  }
  return false;
}

/** UI-only; allocates. */
export function moduleDisableReasons(world: World, ship: Ship, m: ShipModule): string[] {
  const ctx = ctxOf(world, ship, m);
  const reasons: string[] = [];
  for (let i = 0; i < ALL_CHECKS.length; i++) {
    const r = ALL_CHECKS[i](ctx);
    if (r !== null) reasons.push(r);
  }
  return reasons;
}

/** No-cockpit ships fall back to "any module passes" (drones); ships with
 *  destroyed-only cockpits do not fall back. */
export function isShipPowered(world: World, ship: Ship): boolean {
  let sawCockpit = false;
  for (const m of ship.modules) {
    const def = m.def;
    if (def.category !== "cockpit") continue;
    sawCockpit = true;
    if (passesAll(CORE_CHECKS, { world, ship, m, def })) return true;
  }
  if (sawCockpit) return false;
  for (const m of ship.modules) {
    const def = m.def;
    if (passesAll(CORE_CHECKS, { world, ship, m, def })) return true;
  }
  return false;
}
